/**
 * Workspace connections lib (SYN-1520, spec §8.3).
 *
 * Types, fail-closed parsers, and buyer-language presentation for the
 * /workspace/connections surface. The BFF (`GET /api/workspace/connections`)
 * composes Coherence registrations + Atlas connector readiness + listings
 * breakage + role bindings; this module only parses and translates — auth
 * health is never re-derived client-side.
 *
 * Vocabulary (SYN-1502): the four auth statuses arrive as machine enums and
 * MUST translate through AUTH_STATUS_META before rendering. The credit note is
 * the single quiet per-card line the pricing SoT allows — no dollar figures.
 */

import {
  ARTIFACT_ACCESS_SHAPE_VERSION,
  parseArtifactAccess,
  type ArtifactAccess,
  type ArtifactAccessAction,
  type ArtifactSetupState,
} from "./artifact-access";

/**
 * `available_to_connect` — connector has been seed-provisioned in this org but
 * was never authenticated. Calm, non-actionable: renders as "Available to
 * connect" with a Connect action, NOT as "needs attention / Reconnect".
 * Only genuinely expired / revoked / insufficient_scope / drift connectors
 * surface the actionable Reconnect state (SYN-1900).
 */
export type ConnectionAuthStatus =
  | "connected"
  | "expired"
  | "revoked"
  | "action_needed"
  | "available_to_connect";
export type ConnectionReconnectMethod =
  | "oauth_consent"
  | "register"
  | "clerk_external_account"
  | "no_account"
  | "unknown";

export interface ConnectionRoutineRef {
  id: string;
  name: string;
}

/** One named action/tool a sub-bundle contributes to its credential (SYN-1784). */
export interface ConnectionTool {
  name: string;
  label: string;
}

/** A sub-bundle riding on this card's credential-bearing registered_api (SYN-1779). */
export interface ConnectionSubBundle {
  ref: string;
  displayName: string;
  toolCount: number;
  /** The specific named actions/tools this sub-bundle contributes (SYN-1784). */
  tools: ConnectionTool[];
}

/**
 * Caller's per-capability authorities for this connection (WS4, SYN-1779,
 * ACSS §13.4/§13.5.x). SERVER-authoritative; the UI renders controls
 * fail-closed from these — absent/unresolved ⇒ all false ⇒ read-only.
 */
export interface ConnectionRemoveFacts {
  label: string;
  detail: string | null;
  lastUsed: string | null;
  lastUsedScope: "app" | "connection";
}

export interface ConnectionCapabilities {
  /**
   * SYN-3316 (Maria, 3 Oct 2026): this caller made this connection and may Remove it (disconnect
   * and delete in one step). Only an explicit `true` from the server means yes.
   */
  canRemove: boolean;
  canRevoke: boolean;
  canConfigure: boolean;
  canAuthorize: boolean;
  canRegister: boolean;
}

/**
 * One connected-system card at registered_api grain (SYN-1779): ONE entry per
 * credential-bearing registered_api, aggregating the sub-bundles riding its
 * credential. `ref` is the registered_api ref — connect / disconnect /
 * configure act on it. Health (`authStatus`) is SERVER-authoritative.
 */
export interface ConnectionCard {
  connectionId?: string | null;
  ref: string;
  displayName: string;
  authStatus: ConnectionAuthStatus;
  statusDetail: string | null;
  scopeSummary: string;
  /** Auth method for the credential (oauth, api key, …); null when unknown. */
  authType: string | null;
  /** Clerk provider slug for clerk_oauth connections, e.g. "microsoft". */
  authProvider: string | null;
  /** Explicit OAuth authorize URL, when Coherence has one. */
  authorizeUrl: string | null;
  /** OAuth scopes Coherence says this connector requires. */
  requiredScopes: string[];
  /** Server-published reconnect flow hint. */
  reconnectMethod: ConnectionReconnectMethod;
  rolesUsing: string[];
  lastUsed: string | null;
  /** Sub-bundles riding this credential (blast radius, WS2). */
  subBundles: ConnectionSubBundle[];
  /**
   * What this connection can REACH — `{mail: "available", calendar: "unavailable"}`.
   *
   * 🔑 NOT `capabilities` on this same model, which is the caller's AUTHORITY
   * (`canRevoke`/`canConfigure`/`canAuthorize`/`canRegister`). Reach is about the
   * VENDOR; authority is about the CALLER. Full authority over a connection that
   * reaches nothing is a real state, so they may not share a word.
   *
   * ⚠️ `null` means WE HAVE NEVER BEEN TOLD, never "reaches nothing".
   */
  reachableServices: Record<string, string> | null;
  /** Total tools across all sub-bundles on this credential (blast radius). */
  toolCount: number;
  routinesUsing: ConnectionRoutineRef[];
  breaks: ConnectionRoutineRef[];
  registered: boolean;
  /**
   * The held connection this card IS, when it is one (SYN-2844, plumbed SYN-2627).
   *
   * 🔑 THIS IS "THE ORG HOLDS A CREDENTIAL", SEPARATELY FROM "WE COULD READ ITS HEALTH".
   * The server has published it since PR #247; nothing on the client read it. Without it,
   * the only way to ask whether an app is one the org HAS was `authStatus`, which is
   * derived from live readiness — so a leg that came back empty demoted every card and the
   * Apps list quietly lost apps the org genuinely holds. Null for an api-grain bundle card
   * and for an evidence-only one.
   */
  connection: ConnectionIdentity | null;
  /**
   * The sign-in name of the account this connection is bound to (SYN-2891).
   *
   * 🔑 "Which account is this?" is the ONE question the repair dialog could not answer.
   * The server has carried the value since #105 and SYN-2941 folded it into the card
   * TITLE as a suffix — good for telling two rows apart in a list, useless anywhere the
   * customer has to decide something about the account, because a suffix inside a
   * heading cannot be labelled and cannot be asserted on.
   *
   * ⚠️ `null` means WE WERE NEVER TOLD, never "no account". The server deliberately does
   * not fall back to an identifier under this name — see its note on the same field.
   */
  accountLabel: string | null;
  /** Caller's per-capability authorities (WS4, fail-closed). */
  capabilities: ConnectionCapabilities;
  /**
   * SYN-3316 R7: what tells this connection apart on the Remove confirm. `lastUsed` is the last
   * use OF `lastUsedScope` (per app today, SYN-1809), and null means "not recorded", never
   * "never used". Null when the server sent nothing usable.
   */
  removeFacts: ConnectionRemoveFacts | null;
  artifactAccess: ArtifactAccess;
}

/** The connection instance behind a held-connection card (SYN-2844). */
export interface ConnectionIdentity {
  apiId?: string;
  id: string;
  /** Present only for a retained personal Xero binding. */
  bindingVersion?: number;
  subjectType: string | null;
  subjectId: string | null;
  /**
   * The server's answer to "is this the signed-in person's OWN connection?" (D-28). Only a
   * literal `true` from the server makes it so; anything else is false (fail closed), so an
   * older server that never says reads as "not yours" rather than as everyone's.
   */
  ownedByCaller: boolean;
}

/** Fail-closed: anything without a non-empty id is NOT a held connection. */
export function parseConnectionIdentity(raw: unknown): ConnectionIdentity | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === "string" && record.id.length > 0 ? record.id : null;
  if (!id) {
    return null;
  }
  return {
    id,
    ...(typeof record.apiId === "string" && record.apiId.length > 0 ? { apiId: record.apiId } : {}),
    ...(typeof record.bindingVersion === "number" &&
    Number.isSafeInteger(record.bindingVersion) &&
    record.bindingVersion > 0
      ? { bindingVersion: record.bindingVersion }
      : {}),
    subjectType: typeof record.subjectType === "string" ? record.subjectType : null,
    subjectId: typeof record.subjectId === "string" ? record.subjectId : null,
    ownedByCaller: record.ownedByCaller === true,
  };
}

export interface ConnectionRegistration {
  connectionId?: string | null;
  id: string;
  ref: string;
  displayName: string;
  description: string | null;
  authType: string | null;
  authProvider: string | null;
  authorizeUrl: string | null;
  requiredScopes: string[];
  reconnectMethod: ConnectionReconnectMethod;
  unlocks: ConnectionRoutineRef[];
  alreadyConnected: boolean;
  artifactAccess: ArtifactAccess;
}

export interface ConnectionsPayload {
  connections: ConnectionCard[];
  registrations: ConnectionRegistration[];
  errors: Record<string, string | null>;
  target: ConnectionTargetResolution | null;
}

export type ConnectionTargetResolution =
  | { state: "resolved"; connectionId: string; registration: ConnectionRegistration }
  | { state: "not_found"; connectionId: string; recovery: "browse_connections" }
  | { state: "unavailable"; connectionId: string; recovery: "retry" };

/** Spec §8.3 / §7 statuses in buyer words. Reauthorize shows for every genuinely-broken state. */
export const AUTH_STATUS_META: Record<
  ConnectionAuthStatus,
  { label: string; description: string; actionable: boolean; badgeClass: string; dotClass: string }
> = {
  connected: {
    label: "Connected",
    description: "Working normally.",
    actionable: false,
    badgeClass:
      "bg-emerald-500/10 text-emerald-600 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-900",
    dotClass: "bg-emerald-500",
  },
  /** Seed-provisioned but never authenticated — calm, not an alarm (SYN-1900). */
  available_to_connect: {
    label: "Available to connect",
    description: "Sign in to start using this system.",
    actionable: false,
    badgeClass:
      "bg-slate-500/10 text-slate-600 border-slate-200 dark:bg-slate-500/15 dark:text-slate-400 dark:border-slate-700",
    dotClass: "bg-slate-400",
  },
  expired: {
    label: "Expired",
    description: "The sign-in for this system has expired.",
    actionable: true,
    badgeClass:
      "bg-orange-500/10 text-orange-600 border-orange-200 dark:bg-orange-500/15 dark:text-orange-400 dark:border-orange-900",
    dotClass: "bg-orange-500",
  },
  revoked: {
    label: "Revoked",
    description: "Access to this system was revoked.",
    actionable: true,
    badgeClass:
      "bg-red-500/10 text-red-600 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-900",
    dotClass: "bg-red-500",
  },
  action_needed: {
    label: "Action needed",
    description: "This connection needs attention before routines can use it.",
    actionable: true,
    badgeClass:
      "bg-amber-500/10 text-amber-600 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-900",
    dotClass: "bg-amber-500",
  },
};

/**
 * The one quiet credit-relevance note per card (spec §8.3 / §3, pricing SoT).
 * Plain English: tells the admin that this connection counts against their
 * plan's connected-systems limit. The exact number ("3 of 10") is BFF-supplied
 * in the billing payload, not available per-card (SYN-1900 §3).
 */
export const CONNECTION_CREDIT_NOTE = "Counts toward your plan's connected systems.";

const AUTH_STATUSES = new Set<ConnectionAuthStatus>([
  "connected",
  "available_to_connect",
  "expired",
  "revoked",
  "action_needed",
]);
const RECONNECT_METHODS = new Set<ConnectionReconnectMethod>([
  "oauth_consent",
  "register",
  "clerk_external_account",
  "no_account",
  "unknown",
]);

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseRoutineRefs(value: unknown): ConnectionRoutineRef[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: ConnectionRoutineRef[] = [];
  for (const raw of value) {
    const record = asRecord(raw);
    const id = str(record, "id");
    const name = str(record, "name");
    if (id && name) {
      out.push({ id, name });
    }
  }
  return out;
}

function parseStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.length > 0)
    : [];
}

function reconnectMethod(value: unknown): ConnectionReconnectMethod {
  return typeof value === "string" && RECONNECT_METHODS.has(value as ConnectionReconnectMethod)
    ? (value as ConnectionReconnectMethod)
    : "unknown";
}

function num(record: Record<string, unknown>, key: string): number {
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function bool(record: Record<string, unknown>, key: string): boolean {
  return record[key] === true;
}

function parseTools(value: unknown): ConnectionTool[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: ConnectionTool[] = [];
  for (const raw of value) {
    const record = asRecord(raw);
    const name = str(record, "name");
    if (name) {
      out.push({ name, label: str(record, "label") ?? name });
    }
  }
  return out;
}

function parseSubBundles(value: unknown): ConnectionSubBundle[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: ConnectionSubBundle[] = [];
  for (const raw of value) {
    const record = asRecord(raw);
    const ref = str(record, "ref");
    const displayName = str(record, "displayName");
    if (ref && displayName) {
      out.push({
        ref,
        displayName,
        toolCount: num(record, "toolCount"),
        tools: parseTools(record.tools),
      });
    }
  }
  return out;
}

/** Per-cap authorities — fail closed: a missing block ⇒ everything false. */
function parseRemoveFacts(value: unknown): ConnectionRemoveFacts | null {
  const record = asRecord(value);
  const label = str(record, "label");
  const scope = record.lastUsedScope;
  if (!label || (scope !== "app" && scope !== "connection")) {
    return null;
  }
  return {
    label,
    detail: str(record, "detail"),
    lastUsed: str(record, "lastUsed"),
    lastUsedScope: scope,
  };
}

function parseCapabilities(value: unknown): ConnectionCapabilities {
  const record = asRecord(value);
  return {
    canRemove: bool(record, "canRemove"),
    canRevoke: bool(record, "canRevoke"),
    canConfigure: bool(record, "canConfigure"),
    canAuthorize: bool(record, "canAuthorize"),
    canRegister: bool(record, "canRegister"),
  };
}

/**
 * Resolve the rendered auth status from the raw BFF status string + the
 * `registered` flag (SYN-1900).
 *
 * The pivot is `registered`:
 *  - registered===true  → a real Coherence credential exists. Keep the BFF
 *    status as-is (including the fail-safe "action_needed" the BFF emits when
 *    live readiness is missing); an unknown/absent status fails closed to
 *    "action_needed". These are genuinely actionable.
 *  - registered===false → an evidence-only / never-authenticated seed connector
 *    (no credential, no binding). The BFF emits these with authStatus
 *    "action_needed" (handlers/connections.ts:1012) which would otherwise paint
 *    a brand-new org's ~20 seed connectors as needs-attention / Reconnect. They
 *    are not broken, just not connected yet → map to "available_to_connect"
 *    (calm, non-actionable). The ONLY exception is a genuinely terminal state
 *    the BFF asserts explicitly (expired / revoked) — honour those even when
 *    unregistered so a revoked-then-unregistered connector still reads as
 *    actionable, never silently calmed.
 *
 * Only expired / revoked (and the registered action_needed/insufficient_scope/
 * drift paths) surface the actionable Reconnect state.
 */
function resolveAuthStatus(rawStatus: string | null, isRegistered: boolean): ConnectionAuthStatus {
  const recognised =
    rawStatus && AUTH_STATUSES.has(rawStatus as ConnectionAuthStatus)
      ? (rawStatus as ConnectionAuthStatus)
      : null;

  if (isRegistered) {
    // Real credential: keep recognised status; fail closed otherwise.
    return recognised ?? "action_needed";
  }

  // Unregistered seed connector. expired / revoked are terminal states the BFF
  // asserts explicitly — keep them actionable. Everything else (action_needed,
  // available_to_connect, or absent/unknown) is a never-authenticated seed:
  // render it calm as "available_to_connect".
  if (recognised === "expired" || recognised === "revoked") {
    return recognised;
  }
  return "available_to_connect";
}

/**
 * Reach, defensively. The wire is JSON from a column nothing constrains, so a
 * non-string value is DROPPED rather than rendered — an object reaches the screen
 * as `[object Object]`.
 *
 * Absent/unparseable -> `null` (we were never told). An object that parses to no
 * showable pairs stays `{}`, which is a different statement: something was recorded
 * and none of it could be shown.
 */
export function parseReachableServices(raw: unknown): Record<string, string> | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string") {
      out[name] = value;
    }
  }
  return out;
}

export function parseConnectionCard(raw: unknown): ConnectionCard | null {
  const record = asRecord(raw);
  const ref = str(record, "ref");
  const displayName = str(record, "displayName");
  if (!ref || !displayName) {
    return null;
  }
  const status = str(record, "authStatus");
  const isRegistered = record.registered === true;
  const base = {
    connectionId: str(record, "connectionId"),
    ref,
    displayName,
    authStatus: resolveAuthStatus(status, isRegistered),
    statusDetail: str(record, "statusDetail"),
    scopeSummary: str(record, "scopeSummary") ?? "",
    authType: str(record, "authType"),
    authProvider: str(record, "authProvider"),
    authorizeUrl: str(record, "authorizeUrl"),
    requiredScopes: parseStrings(record.requiredScopes),
    reconnectMethod: reconnectMethod(record.reconnectMethod),
    rolesUsing: parseStrings(record.rolesUsing),
    lastUsed: str(record, "lastUsed"),
    subBundles: parseSubBundles(record.subBundles),
    reachableServices: parseReachableServices(record.reachableServices),
    toolCount: num(record, "toolCount"),
    routinesUsing: parseRoutineRefs(record.routinesUsing),
    breaks: parseRoutineRefs(record.breaks),
    registered: record.registered === true,
    connection: parseConnectionIdentity(record.connection),
    accountLabel: str(record, "accountLabel"),
    capabilities: parseCapabilities(record.capabilities),
    removeFacts: parseRemoveFacts(record.removeFacts),
  };
  return {
    ...base,
    artifactAccess:
      parseArtifactAccess(record.artifactAccess) ?? deriveConnectionArtifactAccess(base),
  };
}

export function parseConnectionRegistration(raw: unknown): ConnectionRegistration | null {
  const record = asRecord(raw);
  const id = str(record, "id");
  const ref = str(record, "ref");
  const displayName = str(record, "displayName");
  if (!id || !ref || !displayName) {
    return null;
  }
  const base = {
    connectionId: str(record, "connectionId"),
    id,
    ref,
    displayName,
    description: str(record, "description"),
    authType: str(record, "authType"),
    authProvider: str(record, "authProvider"),
    authorizeUrl: str(record, "authorizeUrl"),
    requiredScopes: parseStrings(record.requiredScopes),
    reconnectMethod: reconnectMethod(record.reconnectMethod),
    unlocks: parseRoutineRefs(record.unlocks),
    alreadyConnected: record.alreadyConnected === true,
  };
  return {
    ...base,
    artifactAccess:
      parseArtifactAccess(record.artifactAccess) ?? deriveRegistrationArtifactAccess(base),
  };
}

export function parseConnectionsPayload(raw: unknown): ConnectionsPayload {
  const record = asRecord(raw);
  const connections = Array.isArray(record.connections)
    ? record.connections
        .map(parseConnectionCard)
        .filter((card): card is ConnectionCard => card !== null)
    : [];
  const registrations = Array.isArray(record.registrations)
    ? record.registrations
        .map(parseConnectionRegistration)
        .filter((reg): reg is ConnectionRegistration => reg !== null)
    : [];
  const errors: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(asRecord(record.errors))) {
    errors[key] = typeof value === "string" ? value : null;
  }
  const targetRecord = asRecord(record.target);
  const targetConnectionId = str(targetRecord, "connectionId");
  const targetState = str(targetRecord, "state");
  let target: ConnectionTargetResolution | null = null;
  if (targetConnectionId && targetState === "resolved") {
    const registration = parseConnectionRegistration(targetRecord.registration);
    if (registration && registration.connectionId === targetConnectionId) {
      target = { state: "resolved", connectionId: targetConnectionId, registration };
    }
  } else if (targetConnectionId && targetState === "not_found") {
    target = {
      state: "not_found",
      connectionId: targetConnectionId,
      recovery: "browse_connections",
    };
  } else if (targetConnectionId && targetState === "unavailable") {
    target = { state: "unavailable", connectionId: targetConnectionId, recovery: "retry" };
  }
  return { connections, registrations, errors, target };
}

/** Roles sentence for a card: names when scoped, plain default otherwise. */
export function rolesSentence(card: ConnectionCard): string {
  if (card.rolesUsing.length === 0) {
    return "Available to the whole workspace";
  }
  return `Used by ${card.rolesUsing.join(", ")}`;
}

function connectionArtifactSetupState(status: ConnectionAuthStatus): ArtifactSetupState {
  switch (status) {
    case "connected":
      return "ready";
    // Never-authenticated seed connectors: not yet set up, but not broken (SYN-1900).
    case "available_to_connect":
    case "action_needed":
      return "needs_setup";
    case "expired":
    case "revoked":
      return "problem";
  }
}

function deriveConnectionArtifactAccess(
  card: Omit<ConnectionCard, "artifactAccess">,
): ArtifactAccess {
  const setupState = connectionArtifactSetupState(card.authStatus);
  const actions: ArtifactAccessAction[] = ["add_to_role_grant_profile"];
  if (setupState !== "ready") {
    actions.unshift("finish_setup");
  }
  return {
    shapeVersion: ARTIFACT_ACCESS_SHAPE_VERSION,
    kind: "connection",
    id: card.connectionId ?? card.ref,
    title: card.displayName,
    description: card.scopeSummary,
    catalogState: "listed",
    accessState: card.rolesUsing.length > 0 ? "assigned" : "yours",
    setupState,
    authoritySource: "coherence",
    roles: card.rolesUsing.map((name) => ({ name })),
    actions,
  };
}

function deriveRegistrationArtifactAccess(
  registration: Omit<ConnectionRegistration, "artifactAccess">,
): ArtifactAccess {
  return {
    shapeVersion: ARTIFACT_ACCESS_SHAPE_VERSION,
    kind: "connection",
    id: registration.connectionId ?? registration.ref,
    title: registration.displayName,
    ...(registration.description ? { description: registration.description } : {}),
    catalogState: registration.alreadyConnected ? "listed" : "discoverable",
    accessState: registration.alreadyConnected ? "yours" : "requestable",
    setupState: registration.alreadyConnected ? "ready" : "needs_setup",
    authoritySource: "coherence",
    roles: [],
    actions: registration.alreadyConnected ? ["add_to_role_grant_profile"] : ["finish_setup"],
  };
}

export function connectionArtifactAccess(card: ConnectionCard): ArtifactAccess {
  if (card.artifactAccess) {
    return card.artifactAccess;
  }
  const { artifactAccess: _artifactAccess, ...base } = card;
  return deriveConnectionArtifactAccess(base);
}

export function connectionRegistrationArtifactAccess(
  registration: ConnectionRegistration,
): ArtifactAccess {
  if (registration.artifactAccess) {
    return registration.artifactAccess;
  }
  const { artifactAccess: _artifactAccess, ...base } = registration;
  return deriveRegistrationArtifactAccess(base);
}

/** Consent/auth URL out of a lifecycle pass-through output, if present. */
export function consentUrlFrom(output: unknown): string | null {
  const genericStart = connectionStartFrom(output);
  if (genericStart?.kind === "redirect" && genericStart.url) {
    return genericStart.url;
  }
  const record = asRecord(output);
  for (const key of ["consent_url", "consentUrl", "auth_url", "authUrl", "url"]) {
    const value = record[key];
    if (typeof value === "string" && /^https?:\/\//.test(value)) {
      return value;
    }
  }
  return null;
}

export interface ConnectionStartEnvelope {
  kind: "redirect" | "clerk_external_account" | "credential_form" | "connected" | "blocked";
  url?: string;
  provider?: string;
  scopes: string[];
  /**
   * SYN-3043. THREE states: absent (nobody was asked), `null` (the connector says there is
   * nothing to type), or the form. Only the first licenses a surface to draw a hardcoded box.
   */
  credentialSchema?: Record<string, unknown> | null;
  reason?: string;
}

/**
 * The declared form off a start envelope, camel first, `null` preserved.
 *
 * 🔑 CAMEL FIRST, MATCHING `credentialSchemaFrom`. Two readers of one envelope that disagree
 * about precedence is two answers to one question (principle 7), and on a mixed envelope they
 * would draw different cards on different screens for the same connector.
 */
function spelledCredentialSchema(
  start: Record<string, unknown>,
): Record<string, unknown> | null | undefined {
  for (const key of ["credentialSchema", "credential_schema"] as const) {
    if (start[key] === undefined) {
      continue;
    }
    const value = start[key];
    if (value === null) {
      return null;
    }
    // 🪤 UNREADABLE IS ABSENT, NOT THE CONNECTOR'S `null` — and it has to be the same answer
    // the SIGNAL path gives, or one surface falls back to a box while the other says "nothing
    // to type" about the identical envelope. `sanitizeCredentialSchema` returns `undefined` for
    // a value it cannot parse ("we were told something we cannot read" is nearer to "nobody
    // told us"); this says the same thing. A third review pass found the two disagreeing.
    return typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;
  }
  return undefined;
}

/** Parse Coherence's non-secret generic start envelope without guessing a flow. */
export function connectionStartFrom(output: unknown): ConnectionStartEnvelope | null {
  const root = asRecord(output);
  const data = Object.keys(asRecord(root.data)).length > 0 ? asRecord(root.data) : root;
  const start = asRecord(data.connection_start ?? data.connectionStart);
  const kind = str(start, "kind");
  if (
    kind !== "redirect" &&
    kind !== "clerk_external_account" &&
    kind !== "credential_form" &&
    kind !== "connected" &&
    kind !== "blocked"
  ) {
    return null;
  }
  const url = str(start, "url");
  return {
    kind,
    ...(url && /^https?:\/\//.test(url) ? { url } : {}),
    ...(str(start, "provider") ? { provider: str(start, "provider")! } : {}),
    scopes: parseStrings(start.scopes),
    // 🔴 SYN-3043. THREE STATES, AND THIS LINE USED TO CARRY ONE.
    //
    // It was `Object.keys(asRecord(a ?? b)).length > 0`, which fails twice over: `??` falls
    // through on `null` as readily as on `undefined`, and `asRecord(null)` is `{}`, whose key
    // count is 0 — so the connector's explicit answer of *this is not set up by typing fields*
    // was dropped entirely and read downstream as "nobody told us", the one state that licenses
    // a surface to invent a password box. It also preferred the SNAKE spelling where
    // `credentialSchemaFrom` prefers CAMEL, so the two disagreed on a mixed envelope.
    //
    // ⇒ Select on `!== undefined`, camel first, matching the shared reader; carry `null`.
    ...(spelledCredentialSchema(start) !== undefined
      ? { credentialSchema: spelledCredentialSchema(start) }
      : {}),
    ...(str(start, "reason") ? { reason: str(start, "reason")! } : {}),
  };
}

/** Verification verdict out of a lifecycle test output — fail closed. */
export function verifiedFrom(output: unknown): boolean {
  const record = asRecord(output);
  return record.verified === true || record.ok === true || record.connected === true;
}
